import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import {
  GetCustomerCheckoutFormDataResponsePatient,
  LogInFormPersonType,
} from "@Shape-Digital/kudzu-data/lib/types/actions";
import React, {
  FC,
  FocusEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { alpha } from "@mui/material";
import { Form, useFormikContext } from "formik";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { useIntl } from "react-intl";
import useCustomerLinkedPatients, {
  LinkedPatient,
} from "../AppointmentCheckoutScreen/useCustomerLinkedPatients";
import Grid from "../../Unknown/Grid";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import Button from "../../Unknown/Button";
import DashedButton from "../../Unknown/DashedButton";
import TextField from "../../Unknown/TextField";
import FormikTextField from "../../Unknown/FormikTextField";
import FormikPhoneInput from "../../Unknown/FormikPhoneInput";
import InputAdornment from "../../Unknown/InputAdornment";
import CircularProgress from "../../Unknown/CircularProgress";
import FormikRadioGroup, {
  FormikRadioGroupOption,
} from "../../Unknown/FormikRadioGroup";
import { SupervisorAccountIcon } from "../../Unknown/Icons";
import formatEmail from "../../../common/formatEmail";
import {
  PatientAgeAlert,
  PatientAgeAlertType,
} from "../../Patient/PatientAgeAlert";
import { GuardianFormValues } from "../AppointmentCheckoutGuardianForm";
import { CHECKOUT_DATE_FORMAT } from "../AppointmentCheckoutFormSectionWrapper/getValidationSchema";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import { useAuthContext } from "../../Auth/AuthContextProvider";
import { useUIContext } from "../../Unknown/UIContext";
import commonMessages from "../../../common/messages";
import AppointmentCheckoutAddPatientModal from "../AppointmentCheckoutAddPatientModal";
import useTranslations from "./useTranslations";

type LocalPatient = {
  id: string;
  first_name: string;
  last_name: string;
  date_birth: string;
  legal_guardian_id: string | null;
  family_contact_id: string | null;
  isLocal: true;
};

type PatientListItem = LinkedPatient | LocalPatient;

type AppointmentCheckoutLinkedPatientFormProps = {
  personType: LogInFormPersonType;
  onPersonTypeChange: (type: LogInFormPersonType) => void;
  selectedCustomer?: DatabaseRow<"customers"> | null;
  prefilledPatient?: GetCustomerCheckoutFormDataResponsePatient | null;
  ageAlertType: PatientAgeAlertType | null;
  centerName: string;
  centerPhone: string;
  minOnlineBookingAge: number;
  minSelfBookingAge: number;
  onDateBirthBlur: (dateBirth: string) => void;
  onNotPossibleAnswerClick?: (question: string) => void;
  onEmailBlur?: (email: string, valid: boolean) => void | Promise<void>;
  isEmailLoading?: boolean;
  isEmailInUse?: boolean;
};

const AppointmentCheckoutLinkedPatientForm: FC<
  AppointmentCheckoutLinkedPatientFormProps
> = ({
  personType,
  onPersonTypeChange,
  selectedCustomer,
  prefilledPatient,
  ageAlertType,
  centerName,
  centerPhone,
  minOnlineBookingAge,
  minSelfBookingAge,
  onDateBirthBlur,
  onNotPossibleAnswerClick,
  onEmailBlur,
  isEmailLoading,
  isEmailInUse,
}) => {
  const translations = useTranslations();
  const { formatMessage } = useIntl();
  const {
    values,
    setFieldValue,
    setFieldTouched,
    setValues,
    isSubmitting,
    errors,
    touched,
    handleBlur,
  } = useFormikContext<GuardianFormValues>();
  const { setAlert } = useUIContext();
  const { awsSession } = useAuthContext();
  const [isPhoneInputShown, setIsPhoneInputShown] = useState(
    !!values.additionalPhoneNumber,
  );
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [selectedListItemId, setSelectedListItemId] = useState<
    string | undefined
  >(undefined);

  const createLinkedPatient = useFirebaseAppFunction("createLinkedPatient");

  const loggedInCustomerId = awsSession?.getIdToken()?.payload?.sub as
    | string
    | undefined;
  const effectiveCustomerId = selectedCustomer?.id || loggedInCustomerId;

  const { data: linkedPatients, refetch } =
    useCustomerLinkedPatients(effectiveCustomerId);

  const onDateBirthBlurRef = useRef(onDateBirthBlur);
  useEffect(() => {
    onDateBirthBlurRef.current = onDateBirthBlur;
  });

  // Validate age for pre-filled patient DOB when the linked patients list loads
  useEffect(() => {
    if (!prefilledPatient?.id || !linkedPatients?.length) {
      return;
    }
    const match = linkedPatients.find((p) => p.id === prefilledPatient.id);
    if (match) {
      const dob = format(parseISO(match.date_birth), CHECKOUT_DATE_FORMAT);
      onDateBirthBlurRef.current(dob);
    }
  }, [linkedPatients, prefilledPatient]);

  const hasPrimaryPatient = (linkedPatients || []).some(
    (p) => !p.family_contact_id && !p.legal_guardian_id,
  );

  const allPatients: PatientListItem[] = [...(linkedPatients || [])];

  const onSelectPatient = (patient: PatientListItem) => {
    if (patient.legal_guardian_id) {
      onPersonTypeChange("legalGuardian");
    } else if (patient.family_contact_id) {
      onPersonTypeChange("familyMember");
    }

    setSelectedListItemId(patient.id);
    const dob = format(parseISO(patient.date_birth), CHECKOUT_DATE_FORMAT);

    setValues((prev) => ({
      ...prev,
      selectedPatientId: !("isLocal" in patient) ? patient.id : undefined,
      firstName: patient.first_name,
      lastName: patient.last_name,
      dateOfBirth: dob,
    }));
    setFieldTouched("firstName", true, false);
    setFieldTouched("lastName", true, false);
    setFieldTouched("dateOfBirth", true, false);
    onDateBirthBlur(dob);
  };

  const onAddPatientSave = async (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    isLegalGuardian: boolean,
    isFamilyMember: boolean,
  ) => {
    try {
      // If legal guardian checked → set both ids (legalGuardian: true)
      // If family member checked OR neither checked but primary patient exists → set only family_contact_id (legalGuardian: false)
      // If neither checked and no primary patient → patient type, no ids
      // NOTE: the last case requires backend support for familyMember: false param
      // (currently createLinkedPatient always sets family_contact_id when legalGuardian is false)
      const effectiveLegalGuardian = isLegalGuardian;
      // legal guardian always implies family member; also default to family member
      // when no checkbox is checked but a primary patient already exists
      const effectiveFamilyMember =
        isLegalGuardian || isFamilyMember || hasPrimaryPatient;

      await createLinkedPatient({
        firstName,
        lastName,
        dateOfBirth,
        legalGuardian: effectiveLegalGuardian,
        familyMember: effectiveFamilyMember,
        customerId: selectedCustomer?.id,
      });

      refetch();
      setIsAddPatientModalOpen(false);
    } catch {
      setAlert({
        severity: "error",
        message: formatMessage(commonMessages.defaultError),
        isShown: true,
      });
    }
  };

  const handleEmailBlur: FocusEventHandler<HTMLInputElement> = async (e) => {
    if (onEmailBlur) {
      onEmailBlur(e.target.value, !errors.guardianEmail);
    }
    handleBlur(e);
  };

  const isEmailInUseError = isEmailInUse ? translations.authErrorMessage : null;

  const onWearingDeviceClick = () => {
    if (onNotPossibleAnswerClick) {
      onNotPossibleAnswerClick(translations.wearingDeviceHeader);
    }
  };

  const wearingDeviceOptions: FormikRadioGroupOption[] = [
    {
      value: "yes",
      label: translations.answerYes,
      onClick: onWearingDeviceClick,
    },
    { value: "no", label: translations.answerNo },
  ];

  return (
    <Form>
      <Grid container spacing={8} direction="column">
        {/* Your information */}
        <Grid item container spacing={4} direction="column">
          <Grid item>
            <Typography variant="h3">
              {translations.yourInformationHeader}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="guardianEmail"
              type="email"
              onChange={({ target }) =>
                setFieldValue("guardianEmail", formatEmail(target.value))
              }
              onBlur={handleEmailBlur}
              value={formatEmail(values.guardianEmail)}
              label={translations.emailLabel}
              disabled={isEmailLoading || isSubmitting}
              fullWidth
              InputProps={{
                endAdornment: isEmailLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : undefined,
              }}
              InputLabelProps={{ shrink: !!values.guardianEmail }}
              error={
                touched.guardianEmail &&
                Boolean(errors.guardianEmail || isEmailInUseError)
              }
              helperText={
                touched.guardianEmail &&
                (errors.guardianEmail || isEmailInUseError)
              }
              autoComplete="off"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikPhoneInput
              name="guardianPhoneNumber"
              fullWidth
              label={translations.phoneNumberLabel}
              disabled={isSubmitting}
            />
          </Grid>
          {isPhoneInputShown ? (
            <Grid item xs={12}>
              <FormikPhoneInput
                name="additionalPhoneNumber"
                fullWidth
                label={translations.additionalPhoneLabel}
                disabled={isSubmitting}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <DashedButton
                fullWidth
                disabled={isSubmitting}
                onClick={() => setIsPhoneInputShown(true)}
              >
                {translations.addAdditionalPhone}
              </DashedButton>
            </Grid>
          )}
        </Grid>

        {/* Select patient */}
        <Grid item container spacing={4} direction="column">
          <Grid item>
            <Typography variant="h3">
              {translations.selectPatientHeader}
            </Typography>
          </Grid>
          {allPatients.map((patient) => {
            const isWard = !!patient.legal_guardian_id;
            const isSelected = selectedListItemId === patient.id;

            return (
              <Grid item key={patient.id}>
                <Box
                  border={1}
                  borderRadius={1}
                  px={4}
                  py={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    borderColor: isSelected ? "success.main" : "divider",
                    bgcolor: isSelected
                      ? (theme) => alpha(theme.palette.success.main, 0.1)
                      : "transparent",
                  }}
                >
                  <Box>
                    <Typography>
                      {patient.first_name} {patient.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(parseISO(patient.date_birth), "MM/dd/yyyy")}
                    </Typography>
                    {isWard && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mt={0.5}
                      >
                        <SupervisorAccountIcon
                          fontSize="small"
                          color="action"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {translations.requiresGuardian}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!isSelected && (
                    <Button
                      variant="contained"
                      onClick={() => onSelectPatient(patient)}
                      disabled={isSubmitting}
                    >
                      {translations.selectButton}
                    </Button>
                  )}
                </Box>
              </Grid>
            );
          })}
          {ageAlertType && (
            <Grid item>
              <PatientAgeAlert
                type={ageAlertType}
                minOnlineBookingAge={minOnlineBookingAge}
                minSelfBookingAge={minSelfBookingAge}
                centerPhone={centerPhone}
                centerName={centerName}
              />
            </Grid>
          )}
          <Grid item>
            <DashedButton
              fullWidth
              disabled={isSubmitting}
              onClick={() => setIsAddPatientModalOpen(true)}
            >
              {translations.addAnotherPatient}
            </DashedButton>
          </Grid>
        </Grid>

        {/* Patient medical information */}
        <Grid item container spacing={4} direction="column">
          <Grid item>
            <Typography variant="h3">
              {translations.patientMedicalHeader}
            </Typography>
          </Grid>
          <Grid item container direction="row" wrap="nowrap" spacing={4}>
            <Grid item xs={6}>
              <FormikTextField
                required
                fullWidth
                type="number"
                disabled={isSubmitting}
                name="heightFt"
                label={translations.heightFtLabel}
              />
            </Grid>
            <Grid item xs={6}>
              <FormikTextField
                required
                fullWidth
                type="number"
                disabled={isSubmitting}
                name="heightIn"
                label={translations.heightInLabel}
              />
            </Grid>
          </Grid>
          <Grid item xs>
            <FormikTextField
              required
              fullWidth
              type="number"
              disabled={isSubmitting}
              name="weightLbs"
              label={translations.weightLbsLabel}
            />
          </Grid>
        </Grid>

        {/* Pacemaker question */}
        <Grid item container spacing={4} direction="column">
          <Grid item>
            <Typography variant="h3">
              {translations.wearingDeviceHeader}
            </Typography>
          </Grid>
          <Grid item>
            <FormikRadioGroup
              name="wearingDevice"
              isDisabled={isSubmitting}
              options={wearingDeviceOptions}
            />
          </Grid>
        </Grid>
      </Grid>

      <AppointmentCheckoutAddPatientModal
        isOpen={isAddPatientModalOpen}
        personType={personType}
        onClose={() => setIsAddPatientModalOpen(false)}
        onSave={onAddPatientSave}
      />
    </Form>
  );
};

export default AppointmentCheckoutLinkedPatientForm;
